import { useState } from "react";
import { useSpatial } from "./SpatialContext";
import { formatWhen } from "../lib/plannerItems";

const STICKY_COLORS = [
  { bg: "#FEF3C7", border: "#FDE68A", pin: "#F59E0B" },
  { bg: "#DBEAFE", border: "#BFDBFE", pin: "#3B82F6" },
  { bg: "#DCFCE7", border: "#BBF7D0", pin: "#22C55E" },
  { bg: "#FCE7F3", border: "#FBCFE8", pin: "#EC4899" },
  { bg: "#EDE9FE", border: "#DDD6FE", pin: "#8B5CF6" },
  { bg: "#FFEDD5", border: "#FED7AA", pin: "#F97316" },
];

const ROTATE = [-2, 1.5, -1, 2, -1.5, 1];

const PLACEHOLDER_NOTES = [
  { content: "Ask Lumen to add todos here" },
  { content: "or mention action items in chat" },
  { content: "they'll pin here automatically" },
];

const EMPTY_DRAFT = { content: "", date: "", time: "" };

function PushPin({ color }) {
  return (
    <svg width="14" height="18" viewBox="0 0 14 18" style={{ display: "block" }}>
      <circle cx="7" cy="5" r="4.5" fill={color} />
      <circle cx="7" cy="5" r="2.5" fill="white" opacity="0.4" />
      <rect x="6" y="8" width="2" height="8" rx="1" fill={color} opacity="0.7" />
    </svg>
  );
}

const noteShell = (sc, rot, extra = {}) => ({
  background: sc.bg,
  border: `1px solid ${sc.border}`,
  borderRadius: 4,
  boxShadow: "2px 3px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.6)",
  transform: `rotate(${rot}deg)`,
  fontFamily: "'DM Sans', sans-serif",
  ...extra,
});

function StickyNote({ note, sc, rot, width, minHeight, fontSize, onEdit, onToggle, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const when = formatWhen(note);

  return (
    <div className="relative flex-shrink-0" style={{ width }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div style={{ position: "absolute", top: -6, left: "50%", transform: "translateX(-50%)", zIndex: 2 }}>
        <PushPin color={sc.pin} />
      </div>

      {hovered && (
        <div style={{ position: "absolute", top: 2, right: 2, zIndex: 3, display: "flex", gap: 3 }}>
          <button title={note.done ? "Mark as not done" : "Mark as done"}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            style={{
              width: 16, height: 16, borderRadius: "50%", border: "none", cursor: "pointer",
              background: note.done ? "rgba(34,197,94,0.35)" : "rgba(0,0,0,0.18)",
              color: "rgba(0,0,0,0.55)", fontSize: 9, fontWeight: 700, lineHeight: 1,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>✓</button>
          <button title="Delete"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            style={{
              width: 16, height: 16, borderRadius: "50%", border: "none", cursor: "pointer",
              background: "rgba(0,0,0,0.18)", color: "rgba(0,0,0,0.5)", fontSize: 9, fontWeight: 700,
              lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center",
            }}>✕</button>
        </div>
      )}

      <div onClick={onEdit} title="Click to edit"
        style={noteShell(sc, rot, {
          padding: "20px 10px 10px", minHeight, cursor: "pointer",
          opacity: note.done ? 0.55 : 1, transition: "opacity 0.15s", userSelect: "none",
        })}>
        <div style={{
          fontSize, color: "#334155", lineHeight: 1.45,
          textDecoration: note.done ? "line-through" : "none", wordBreak: "break-word",
        }}>
          {note.content}
        </div>
        {when && (
          <div style={{ marginTop: 6, fontSize: 9, fontWeight: 700, color: "#0F766E" }}>{when}</div>
        )}
        {note.done && (
          <div style={{ marginTop: 6, fontSize: 9, color: "#22C55E", fontWeight: 700 }}>✓ done</div>
        )}
      </div>
    </div>
  );
}

const modalField = {
  width: "100%",
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  padding: "5px 7px",
  fontSize: 11,
  color: "#1E293B",
  fontFamily: "'DM Sans', sans-serif",
  outline: "none",
  background: "white",
};

const modalLabel = "text-[9px] font-bold text-slate-400 uppercase tracking-[0.06em] mb-1";

function NoteDialog({ draft, setDraft, error, isNew, onSave, onCancel, onDelete }) {
  const [dateOpen, setDateOpen] = useState(!!draft.date);

  const clearDate = () => { setDateOpen(false); setDraft({ ...draft, date: "", time: "" }); };

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4 font-['DM_Sans']"
      style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(3px)", zIndex: 9998 }}
      onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl w-[260px] p-3.5" onClick={(e) => e.stopPropagation()}>
        <div className="text-[12px] font-bold text-slate-800 mb-2.5">{isNew ? "New note" : "Edit note"}</div>

        <div className="mb-2">
          <div className={modalLabel}>Note</div>
          <textarea autoFocus rows={4} aria-label="Note" value={draft.content}
            placeholder="What needs doing?"
            onChange={(e) => setDraft({ ...draft, content: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSave(); }
              if (e.key === "Escape") onCancel();
            }}
            style={{ ...modalField, resize: "none" }} />
        </div>

        {!dateOpen ? (
          <button onClick={() => setDateOpen(true)}
            className="mb-2.5 bg-none border-none p-0 cursor-pointer text-[10px] font-bold text-[#0F766E]">
            + add a date
          </button>
        ) : (
          <div className="flex gap-2 mb-2.5">
            <div className="flex-1 min-w-0">
              <div className={modalLabel}>Date</div>
              <input type="date" aria-label="Note date" autoFocus value={draft.date}
                onChange={(e) => setDraft({ ...draft, date: e.target.value, time: e.target.value ? draft.time : "" })}
                style={modalField} />
            </div>
            {draft.date && (
              <div className="flex-1 min-w-0">
                <div className={modalLabel}>Time</div>
                <input type="time" aria-label="Note time" value={draft.time}
                  onChange={(e) => setDraft({ ...draft, time: e.target.value })}
                  style={modalField} />
              </div>
            )}
            <div className="flex flex-col">
              <div className={modalLabel} aria-hidden="true">&nbsp;</div>
              <button onClick={clearDate} title="No date"
                className="flex-1 flex items-center bg-none border-none p-0 px-0.5 cursor-pointer text-slate-400 text-[11px] leading-none">
                ✕
              </button>
            </div>
          </div>
        )}

        {error && <div className="text-[10px] text-red-500 mb-2">{error}</div>}

        <div className="flex items-center gap-2">
          <button onClick={onSave}
            className="flex-1 py-1.5 rounded-md bg-[#4DB8B5] text-white text-[11px] font-bold cursor-pointer border-none">
            Save
          </button>
          <button onClick={onCancel}
            className="px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-slate-500 text-[11px] font-semibold cursor-pointer">
            Cancel
          </button>
          {!isNew && (
            <button onClick={onDelete}
              className="px-2.5 py-1.5 rounded-md border border-red-100 bg-white text-red-500 text-[11px] font-semibold cursor-pointer">
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AddNoteCard({ onClick, width, minHeight, disabled, disabledHint }) {
  return (
    <button onClick={onClick} disabled={disabled} title={disabled ? disabledHint : "Add a note"}
      className="flex-shrink-0"
      style={{
        width, minHeight, borderRadius: 4, border: "1px dashed rgba(255,255,255,0.55)",
        background: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.85)", fontSize: 10,
        fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
        fontFamily: "'DM Sans', sans-serif",
      }}>
      + Note
    </button>
  );
}

export default function BulletinNotes({ compact = false }) {
  const {
    surfaceState, currentLocation,
    addBulletinNote, updateBulletinNote, deleteBulletinNote, toggleBulletinItem,
  } = useSpatial();

  const notes = surfaceState?.bulletinItems || [];
  const inRoom = !!currentLocation?.roomId;

  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [error, setError] = useState(null);

  const width = compact ? 110 : 120;
  const minHeight = compact ? 80 : 90;
  const fontSize = compact ? 10 : 11;

  const startNew = () => { setDraft(EMPTY_DRAFT); setError(null); setEditingId("new"); };
  const startEdit = (note) => {
    setDraft({ content: note.content || "", date: note.date || "", time: note.time || "" });
    setError(null);
    setEditingId(note.id);
  };
  const cancel = () => { setEditingId(null); setError(null); };

  const save = () => {
    if (!draft.content.trim()) { setError("Write something first."); return; }
    const payload = { content: draft.content, date: draft.date || null, time: draft.date ? draft.time || null : null };
    const result = editingId === "new" ? addBulletinNote(payload) : updateBulletinNote(editingId, payload);
    if (result?.ok === false) {
      setError(result.reason === "not_found" ? "That note is gone." : "Check the date.");
      return;
    }
    cancel();
  };

  const remove = (noteId) => {
    if (editingId === noteId) cancel();
    deleteBulletinNote(noteId);
  };

  const showPlaceholders = notes.length === 0 && editingId !== "new";

  return (
    <div className={compact ? "flex gap-3 overflow-x-auto pb-1 relative" : "flex flex-wrap gap-5 relative"}>
      {showPlaceholders && PLACEHOLDER_NOTES.map((item, i) => {
        const sc = STICKY_COLORS[i % STICKY_COLORS.length];
        return (
          <div key={`hint-${i}`} className="relative flex-shrink-0" style={{ width }}>
            <div style={{ position: "absolute", top: -6, left: "50%", transform: "translateX(-50%)", zIndex: 2 }}>
              <PushPin color={sc.pin} />
            </div>
            <div style={noteShell(sc, ROTATE[i % ROTATE.length], { padding: "20px 10px 10px", minHeight, opacity: 0.7 })}>
              <div style={{ fontSize: 9, color: "#64748B", fontStyle: "italic", lineHeight: 1.45 }}>{item.content}</div>
            </div>
          </div>
        );
      })}

      {notes.map((note, i) => (
        <StickyNote key={note.id} note={note} sc={STICKY_COLORS[i % STICKY_COLORS.length]}
          rot={ROTATE[i % ROTATE.length]} width={width} minHeight={minHeight}
          fontSize={fontSize} onEdit={() => startEdit(note)} onToggle={() => toggleBulletinItem(note.id)}
          onDelete={() => remove(note.id)} />
      ))}

      <AddNoteCard onClick={startNew} width={width} minHeight={minHeight}
        disabled={!inRoom} disabledHint="Walk into a room to pin a note" />

      {editingId && (
        <NoteDialog draft={draft} setDraft={setDraft} error={error} isNew={editingId === "new"}
          onSave={save} onCancel={cancel} onDelete={() => remove(editingId)} />
      )}
    </div>
  );
}
